import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { languageName } from '~/lib/translations/languageName';
import ArrowOrderIcon from '~/assets/icons/arrow-order.svg';
import { resolveLocale } from '~/ui/locale-selector/LocaleSelector';
import { Overlay } from '~/ui/overlay/Overlay';
import * as S from './LargeLocaleSelector.styled';
import { TextLocalePage } from '~/training/settings/locales/TextLocalePage';
import { Flex } from '~/ui/flex/Flex';
import { SettingsPages, useSettingsStore } from '~/training/settings/Store';
import { getSupportedLanguageLabel } from '~/lib/translations/getSupportedLanguageLabel';
import {
  fetchLearnerLanguagePreference,
  logLmsLanguage,
  useLmsAdapter
} from '~/lms';
import { getLanguagePickerLocales } from '~/training/settings/locales/getLanguagePickerLocales';
import { useArrowFocusList } from '~/lib/a11y/useArrowFocusList';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { t } from 'ttag';

const selector = (s: TrainingState) =>
  select(
    s,
    'setTextLocale',
    'setLocale',
    'textLocale',
    'textLocales',
    'subtitleLocales',
    'mediumLocales',
    'supportedLanguages',
    'languageAutoDetect'
  );

export const LargeLocaleSelector: FC<{
  locales: string[];
  reloadSubtitle?: boolean;
  isOnboarding?: boolean;
}> = ({ locales, reloadSubtitle, isOnboarding }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const selectRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const {
    textLocale,
    setTextLocale,
    setLocale,
    textLocales,
    subtitleLocales,
    mediumLocales,
    supportedLanguages,
    languageAutoDetect
  } = useTrainingStore(selector);
  const { setPage, toggle: toggleSettings } = useSettingsStore();
  const lms = useLmsAdapter();
  // On onboarding, don't seed the dropdown from a stale API `textLocale` —
  // wait for the LMS read (or fall back after it completes).
  const [selectedLocale, setSelectedLocale] = useState(() =>
    isOnboarding ? 'en' : textLocale || 'en'
  );
  const selectableLocales = getLanguagePickerLocales({
    textLocales,
    subtitleLocales,
    mediumLocales
  });

  useArrowFocusList(dialogRef, isModalOpen);
  useFocusTrap(dialogRef, isModalOpen);

  // Keep dropdown label in sync with the store (settings changes, etc.).
  // On onboarding we skip this so a stale API `en` does not overwrite LMS `fr`.
  useEffect(() => {
    if (isOnboarding) return;
    setSelectedLocale(textLocale);
  }, [textLocale, isOnboarding]);

  useEffect(() => {
    const applyLocale = (locale: string) => {
      setSelectedLocale(locale);
      document.documentElement.setAttribute('lang', locale);

      if (isOnboarding) {
        // Match manual pick in TextLocalePage (fullSelector → setLocale) and
        // update textLocale immediately so translations load before the API
        // round-trip. LMS locale wins over a stale server value (e.g. `en`).
        useTrainingStore.setState({ textLocale: locale });
        setLocale(locale);
      } else if (locale !== textLocale) {
        setTextLocale(locale, reloadSubtitle ?? false, false);
      }
    };

    const applyBrowserFallback = () => {
      const detected = languageAutoDetect
        ? window.navigator.language
        : textLocale || 'en';

      let resolved = resolveLocale(detected, locales);

      if (
        languageAutoDetect &&
        textLocale &&
        resolved === 'en' &&
        detected !== textLocale
      ) {
        const fromProfile = resolveLocale(textLocale, locales);
        if (locales.includes(fromProfile)) {
          resolved = fromProfile;
        }
      }

      const finalLocale = locales.includes(resolved)
        ? resolved
        : textLocale && locales.includes(resolveLocale(textLocale, locales))
          ? resolveLocale(textLocale, locales)
          : 'en';

      applyLocale(finalLocale);
    };

    const applyOnboardingApiFallback = () => {
      const fromApi = textLocale
        ? resolveLocale(textLocale, selectableLocales)
        : 'en';
      const fallback = selectableLocales.includes(fromApi) ? fromApi : 'en';
      logLmsLanguage('onboarding fallback to API locale', {
        textLocale,
        fallback
      });
      // UI only — server already has this locale; persist on Continue / manual pick.
      setSelectedLocale(fallback);
      document.documentElement.setAttribute('lang', fallback);
    };

    void (async () => {
      if (lms.isAvailable) {
        try {
          const externalLang = await fetchLearnerLanguagePreference(lms);
          const allowedLocales = isOnboarding ? selectableLocales : locales;

          logLmsLanguage('locale selector LMS result', {
            isOnboarding,
            externalLang: externalLang || '(empty)',
            allowedLocales
          });

          if (externalLang) {
            const resolved = resolveLocale(externalLang, allowedLocales);
            if (allowedLocales.includes(resolved)) {
              logLmsLanguage('onboarding apply LMS locale', {
                resolved,
                previousTextLocale: textLocale
              });
              applyLocale(resolved);
              return;
            }
            logLmsLanguage('LMS locale not in allowed list', {
              resolved,
              allowedLocales
            });
          }
        } catch (error) {
          logLmsLanguage('locale selector LMS read failed', {
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }

      if (isOnboarding) {
        applyOnboardingApiFallback();
        return;
      }

      applyBrowserFallback();
    })();
  }, []);

  const openLanguagePicker = () => {
    setIsModalOpen(true);
    setPage(SettingsPages.TextLocale);
  };

  const closeLanguagePicker = useCallback(() => {
    setIsModalOpen(false);
    // Picker sets page to TextLocale without opening settings; reset so
    // Accessibility Settings does not open on a stale sub-page.
    toggleSettings(false);
    selectRef.current?.focus();
  }, [toggleSettings]);

  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLanguagePicker();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isModalOpen, closeLanguagePicker]);

  const handleLanguagePickerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openLanguagePicker();
    }
  };

  return (
    <>
      <S.Select
        ref={selectRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isModalOpen}
        aria-label={`${languageName(selectedLocale)}. ${getSupportedLanguageLabel(
          selectedLocale,
          supportedLanguages[selectedLocale]
        )}`}
        onClick={openLanguagePicker}
        onKeyDown={handleLanguagePickerKeyDown}
      >
        <S.TextBlock>
          {languageName(selectedLocale)}

          <span>
            {getSupportedLanguageLabel(
              selectedLocale,
              supportedLanguages[selectedLocale]
            )}
          </span>
        </S.TextBlock>

        <ArrowOrderIcon />
      </S.Select>

      <Overlay open={isModalOpen} opacity={0.1}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'stretch', sm: 'center' }}
          alignItems={{ _: 'flex-end', sm: 'center' }}
          overflow="hidden"
        >
          <S.ModalBlock
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={t`Language`}
            className="modalStyleForDarkTheme"
          >
            <TextLocalePage
              onClose={closeLanguagePicker}
              centerTitle
              selectedLocale={selectedLocale}
              setSelectedLocale={
                setSelectedLocale as unknown as (value: string) => void
              }
            />
          </S.ModalBlock>
        </Flex>
      </Overlay>
    </>
  );
};
